import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import type { Value } from "platejs";
import { createSlateEditor } from "platejs";
import { serializeHtml } from "platejs/static";
import { memo, useEffect, useState } from "react";
import type { DraftCollectionItem } from "../../db/collections";
import { chatBasePlugins } from "../message-editor/editor-plugins";

/**
 * Ghost bubble rendered at the end of a chat thread while a draft is in
 * the SENDING state. Mirrors the merchant-side `ChatMessageBubble` styling
 * but shows a "Sending..." label in place of a timestamp so the merchant
 * gets immediate optimistic feedback when hitting send.
 */
export const SendingChatBubble = memo(function SendingChatBubble({
  draft,
}: {
  draft: DraftCollectionItem;
}) {
  const [text, setText] = useState<string>("");

  useEffect(() => {
    try {
      const value = JSON.parse(draft.content) as Value;
      const staticEditor = createSlateEditor({
        plugins: chatBasePlugins,
        value,
      });
      void serializeHtml(staticEditor, {
        stripClassNames: true,
        stripDataAttributes: true,
      }).then((html) => {
        // Strip HTML tags for chat display — chat bubbles render plain text
        const plainText = html.replace(/<[^>]*>/g, "").trim();
        setText(plainText);
      });
    } catch {
      setText("");
    }
  }, [draft.content]);

  const hasAttachments = draft.attachments.length > 0;

  return (
    <Flex align="flex-end" dir="column" gap="xs" pl="5xl">
      <Text fontSize="xs" textColor="tertiary">
        Sending...
      </Text>
      <Flex dir="column" gap="sm" style={{ opacity: 0.7 }}>
        {text && (
          <Flex bgColor="alternate" p="lg" radius="md">
            <Text
              fontSize="sm"
              style={{ wordBreak: "break-word" }}
              textColor="white"
            >
              {text}
            </Text>
          </Flex>
        )}
        {hasAttachments && (
          <Flex dir="column" gap="xs">
            {draft.attachments.map((attachment) => {
              const description =
                attachment.description._tag === "Some"
                  ? attachment.description.value
                  : "Attachment";
              const mimeType =
                attachment.mimeType._tag === "Some"
                  ? attachment.mimeType.value
                  : null;
              const isImage = mimeType?.startsWith("image/");

              return (
                <Flex dir="column" gap="xs" key={attachment.id}>
                  {isImage ? (
                    <img
                      alt={description}
                      src={attachment.url}
                      style={{
                        maxWidth: "300px",
                        maxHeight: "300px",
                        objectFit: "contain",
                        borderRadius: "8px",
                      }}
                    />
                  ) : (
                    <Flex
                      align="center"
                      bgColor="alternate"
                      gap="sm"
                      p="md"
                      radius="md"
                    >
                      <Text fontSize="sm" textColor="white">
                        {description}
                      </Text>
                    </Flex>
                  )}
                </Flex>
              );
            })}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});
